import React, { useState, useMemo, useEffect } from 'react'
import { useClipboard } from '../hooks/useClipboard'
import { CardType } from '../types'
import { STASH_DECK_COMPOSITION, DECK_COMPOSITION, getCardDisplayName } from '../constants'
import { Language } from '../data/translations'

// Build list of all card names for autocomplete
const getAllCardNames = (lang: Language): string[] => {
  const stashNames = STASH_DECK_COMPOSITION.map(ci => getCardDisplayName(ci.type, ci.value, ci.subType, (ci as any).faceSlug, lang))
  const colorNames = DECK_COMPOSITION.map(ci => {
    return getCardDisplayName(ci.type, ci.value, undefined, undefined, lang)
  })
  const unique = [...new Set([...stashNames, ...colorNames])]
  return unique.sort()
}

const SeedManager: React.FC<{
  seed: string;
  onRestart: (seed?: string) => void;
  onAddToHand?: (cardName: string, count: number, target: 'me' | 'ai') => void;
}> = ({ seed, onRestart, onAddToHand }) => {
  const [inputSeed, setInputSeed] = useState('')
  const [cardName, setCardName] = useState('')
  const [cardCount, setCardCount] = useState(1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const { copied, copyText } = useClipboard()
  const showDev = !!onAddToHand

  const [lang, setLang] = useState<Language>(() => (localStorage.getItem('oko_lang') as Language) || 'en')

  useEffect(() => {
    const handleStorage = () => {
      const current = (localStorage.getItem('oko_lang') as Language) || 'en'
      if (current !== lang) setLang(current)
    }
    window.addEventListener('storage', handleStorage)
    const interval = setInterval(handleStorage, 1000) // Polling fallback for same-tab changes
    return () => {
      window.removeEventListener('storage', handleStorage)
      clearInterval(interval)
    }
  }, [lang])

  const allCardNames = useMemo(() => getAllCardNames(lang), [lang])

  const filteredSuggestions = useMemo(() => {
    if (!cardName.trim()) return []
    const norm = cardName.toLowerCase()
    return allCardNames.filter(name => name.toLowerCase().includes(norm)).slice(0, 8)
  }, [cardName, allCardNames])

  const handleSelectSuggestion = (name: string) => {
    setCardName(name)
    setShowSuggestions(false)
  }

  return (
    <div className="w-64 text-white">
      <h3 className="font-bold text-lg w-full text-center bg-black/30 p-2 rounded-lg group-hover:rounded-b-none backdrop-blur-sm cursor-default transition-all duration-500">Seed Manager</h3>
      <div className="max-h-0 overflow-hidden group-hover:max-h-[24rem] transition-all duration-500 ease-in-out">
        <div className="bg-slate-800/70 p-3 rounded-b-lg text-sm backdrop-blur-sm flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="font-bold">Game Seed:</span>
            <div className="flex items-center gap-2">
              <input type="text" readOnly value={seed} className="bg-slate-900/50 rounded px-2 py-1 text-xs w-28 font-mono" />
              <button onClick={() => copyText(seed)} className="bg-slate-600 hover:bg-slate-500 rounded px-2 py-1 text-xs">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="text" placeholder="Paste seed to replay" value={inputSeed} onChange={(e) => setInputSeed(e.target.value)} className="bg-slate-900/50 rounded px-2 py-1 text-xs w-full font-mono" />
            <button onClick={() => onRestart(inputSeed)} disabled={!inputSeed} className="bg-indigo-600 hover:bg-indigo-500 rounded px-2 py-1 text-xs w-2/3 disabled:opacity-50 disabled:cursor-not-allowed">
              Restart with Seed
            </button>
          </div>
          {showDev && (
            <div className="mt-2 flex flex-col gap-2">
              <span className="font-bold">Dev: Add Card</span>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Card name"
                  value={cardName}
                  onChange={(e) => {
                    setCardName(e.target.value)
                    setShowSuggestions(true)
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  className="bg-slate-900/50 rounded px-2 py-1 text-xs w-full font-mono"
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 w-full bg-slate-900 border border-slate-700 rounded-b-lg max-h-40 overflow-y-auto shadow-lg">
                    {filteredSuggestions.map((name, idx) => (
                      <div
                        key={idx}
                        onMouseDown={() => handleSelectSuggestion(name)}
                        className="px-2 py-1 text-xs cursor-pointer hover:bg-slate-700 transition-colors"
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <input type="number" min={1} max={10} value={cardCount} onChange={(e) => setCardCount(parseInt(e.target.value) || 1)} className="bg-slate-900/50 rounded px-2 py-1 text-xs w-full font-mono" />
              <div className="flex items-center gap-2">
                <button onClick={() => onAddToHand && onAddToHand(cardName, cardCount, 'me')} disabled={!cardName} className="bg-green-600 hover:bg-green-500 rounded px-2 py-1 text-xs disabled:opacity-50 disabled:cursor-not-allowed">Me</button>
                <button onClick={() => onAddToHand && onAddToHand(cardName, cardCount, 'ai')} disabled={!cardName} className="bg-red-600 hover:bg-red-500 rounded px-2 py-1 text-xs disabled:opacity-50 disabled:cursor-not-allowed">AI</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SeedManager

